/**
 * SquareGameStyleCharity — Charity Showcase section renderers for GridEngine.
 * Extracted from SquareGameStyleCharity.jsx layout.
 */
import React from 'react'
import Grid3x3 from 'lucide-react/dist/esm/icons/grid-3x3.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import CenterSquareCharity from '../../../components/charity/CenterSquareCharity'
import { CharitySidebar } from '../../../components/CharityHeroSection'
import { charityConfig } from '../../../config/charity'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'

/**
 * Charity Hero Section — Emerald gradient with logo, editable title/subtitle, "Buy a Square" CTA
 */
export function SquareCharityHeroSection({ partner, t, editMode, handleTranslationUpdate, onLoginClick, salesClosed, purchaseNavigate }) {
  const { c } = useContent()
  const navigate = purchaseNavigate || usePurchaseNavigate(onLoginClick)

  return (
    <>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-400/20 to-green-400/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-teal-400/20 to-emerald-400/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
        {partner?.partner_logo_url && (
          <div className="flex justify-center mb-6">
            <img
              src={partner.partner_logo_url}
              alt={partner.partner_name || 'Charity Logo'}
              className="h-16 object-contain bg-white/95 rounded-lg px-4 py-2 shadow"
            />
          </div>
        )}

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white drop-shadow-md mb-4">
          <InlineTranslationEdit
            translationKey="CharityShowcaseHeroTitle"
            value={c('CharityShowcaseHeroTitle', 'Every Square Supports a Cause')}
            fallback="Every Square Supports a Cause"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h1>

        <p className="text-lg md:text-xl text-emerald-50/95 max-w-2xl mx-auto mb-8">
          <InlineTranslationEdit
            translationKey="CharityShowcaseHeroSubtitle"
            value={c(
              'CharityShowcaseHeroSubtitle',
              'Pick a number, back the charity, win the prize.'
            )}
            fallback="Pick a number, back the charity, win the prize."
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>

        <HeroPartnerGlassSelect className="mx-auto mb-8 w-full max-w-md" />

        <button
          onClick={() => navigate('/square-game-purchase')}
          disabled={salesClosed}
          className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-emerald-50 text-emerald-700 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Grid3x3 className="w-5 h-5" />
          <InlineTranslationEdit
            translationKey="CharityShowcaseHeroCta"
            value={c('CharityShowcaseHeroCta', 'Buy a Square')}
            fallback="Buy a Square"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </>
  )
}

/**
 * Charity Hero Spotlight — 2-col: CenterSquareCharity (left) + CharitySidebar (right)
 */
export function SquareCharityHeroSpotlight({ mainDraw, t, editMode, handleTranslationUpdate, sections }) {
  const { c } = useContent()

  const getCharityConfig = () => {
    const charitySection = sections?.find(s => s.section_type === 'charity_hero' && s.is_visible)
    return charitySection?.config || { placement: 'hero_strip', theme: 'square_game', showSidebar: true }
  }

  const grandPrize = mainDraw?.current_jackpot || 5000
  const charityCfg = getCharityConfig()
  const gameData = { jackpot: grandPrize }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
        <InlineTranslationEdit
          translationKey="CharityShowcaseSpotlightHeading"
          value={c('CharityShowcaseSpotlightHeading', "Who You're Supporting")}
          fallback="Who You're Supporting"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        {/* Left: CenterSquareCharity spotlight (span 2) */}
        <div className="lg:col-span-2 h-full flex flex-col">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-emerald-200 dark:border-emerald-800 p-6 md:p-10 h-full flex flex-col">
            <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col justify-center">
              <CenterSquareCharity
                editMode={editMode}
                handleTranslationUpdate={handleTranslationUpdate}
              />
              <p className="mt-6 text-center text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed italic">
                <InlineTranslationEdit
                  translationKey="CharityShowcaseMissionLine"
                  value={c(
                    'CharityShowcaseMissionLine',
                    charityConfig.mission || charityConfig.description || ''
                  )}
                  fallback={charityConfig.mission || charityConfig.description || ''}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          </div>
        </div>

        {/* Right: CharitySidebar (span 1) */}
        <div className="lg:col-span-1 h-full flex flex-col">
          <CharitySidebar config={charityCfg} gameType="squareGame" gameData={gameData} />
        </div>
      </div>
    </div>
  )
}

/**
 * Charity Live Draw Section — Draw date card with countdown and "Pick Your Square"
 */
export function SquareCharityLiveDrawSection({
  mainDraw,
  t,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  timeLeft,
  shouldAnimate,
  countdownStyle,
  salesClosed,
  purchaseNavigate,
}) {
  const { c } = useContent()
  const navigate = purchaseNavigate || usePurchaseNavigate(onLoginClick)

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'Date to be announced'

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          <Calendar className="w-4 h-4" />
          <InlineTranslationEdit
            translationKey="squareGame.drawDate"
            value={c('squareGame.drawDate', 'Draw Date')}
            fallback="Draw Date"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </div>
        <div className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          {drawDateLabel}
        </div>

        <div className="mb-6">
          <CountdownTimerStyles
            timeLeft={timeLeft}
            shouldAnimate={shouldAnimate}
            style={countdownStyle}
          />
        </div>

        <button
          onClick={() => navigate('/square-game-purchase')}
          disabled={salesClosed}
          className="w-full py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Grid3x3 className="w-5 h-5" />
          <InlineTranslationEdit
            translationKey="squareGame.pickSquare"
            value={c('squareGame.pickSquare', 'Pick Your Square')}
            fallback="Pick Your Square"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </button>
      </div>
    </div>
  )
}

/**
 * Charity How It Works Section — 3-step grid with icons (Grid3x3, Heart, Trophy)
 */
export function SquareCharityHowItWorksSection({ mainDraw, t, editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const rawTotal = mainDraw?.config?.gridSize ?? mainDraw?.max_tickets ?? 100
  const totalSquares = Number.isFinite(Number(rawTotal)) && Number(rawTotal) > 0 ? Number(rawTotal) : 100

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
        <InlineTranslationEdit
          translationKey="CharityShowcaseHowItWorksHeading"
          value={c('CharityShowcaseHowItWorksHeading', 'How It Works')}
          fallback="How It Works"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h2>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              Icon: Grid3x3,
              titleKey: 'CharityShowcaseStep1Title',
              titleFallback: 'Pick Your Squares',
              descKey: 'CharityShowcaseStep1Desc',
              descFallback: `Choose from 1 to ${totalSquares} — each square is your shot at the prize.`,
            },
            {
              Icon: Heart,
              titleKey: 'CharityShowcaseStep2Title',
              titleFallback: 'Support the Cause',
              descKey: 'CharityShowcaseStep2Desc',
              descFallback: 'Every square helps fund the charity programs that change lives.',
            },
            {
              Icon: Trophy,
              titleKey: 'CharityShowcaseStep3Title',
              titleFallback: 'Win Together',
              descKey: 'CharityShowcaseStep3Desc',
              descFallback: 'When the draw happens, a winning square takes the grand prize.',
            },
          ].map(({ Icon, titleKey, titleFallback, descKey, descFallback }) => (
            <div key={titleKey} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 shadow-lg mb-4">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                <InlineTranslationEdit
                  translationKey={titleKey}
                  value={c(titleKey, titleFallback)}
                  fallback={titleFallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                <InlineTranslationEdit
                  translationKey={descKey}
                  value={c(descKey, descFallback)}
                  fallback={descFallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
